import { useCallback, useEffect, useRef, useState } from 'react';
import { Navigate, Route, Routes, useNavigate } from 'react-router-dom';
import type { ReactNode } from 'react';
import type { Bootstrap } from '../shared/contracts';
import AppShell, { useAppData } from './components/AppShell';
import { getBootstrap, logout, resetDemo } from './lib/api';
import AccessPage from './pages/AccessPage';
import PlacementPage from './pages/PlacementPage';
import { CanNTalkProvider } from './canntalk/context';
import { LivePractice, PracticeHome, PracticeProgress, ScenarioCatalogue, SessionReport, SessionSetup } from './canntalk/pages';
import ScenarioStudio from './canntalk/ScenarioStudio';
import AIConnectionSettings from './canntalk/AISettings';
import { IdentityProvider, useIdentity } from './canntalk/AccountContext';
import { getIdentity, type IdentityResponse, type AccountRole } from './canntalk/identityApi';
import AccountPage from './canntalk/AccountPage';
import IdentityManagement from './canntalk/IdentityManagement';
import TeachingDashboard from './canntalk/TeachingDashboard';
import { clearDiscussionCaptures } from './canntalk/discussionCapture';
import './canntalk/accounts.css';

function RoleRoute({ roles, children }: { roles: AccountRole[]; children: ReactNode }) { const { identity } = useIdentity(); return roles.includes(identity.user.role) ? children : <Navigate to={identity.user.role === 'teacher' ? '/teaching' : '/dashboard'} replace />; }
function PlacementGate({ children }: { children: ReactNode }) { const { bootstrap } = useAppData(); return bootstrap.placement?.required ? <Navigate to="/placement" replace /> : children; }

export default function App() {
  const [bootstrap, setBootstrap] = useState<Bootstrap | null>(null);
  const [locked, setLocked] = useState(false);
  const [loading, setLoading] = useState(true);
  const [identity, setIdentity] = useState<IdentityResponse | null>(null);
  const navigate = useNavigate();
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  const load = useCallback(async (afterLogin = false) => {
    setLoading(true);
    try {
      const current = await getIdentity();
      setIdentity(current);
      setBootstrap(current.user.mustChangePassword ? null : await getBootstrap());
      setLocked(false);
      if (afterLogin || window.location.pathname === '/' || window.location.pathname === '/today') navigateRef.current(current.user.role === 'teacher' ? '/teaching' : '/dashboard', { replace: true });
    } catch (error) {
      setIdentity(null); setBootstrap(null); setLocked(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => { const expire = () => { clearDiscussionCaptures(); setIdentity(null); setBootstrap(null); setLocked(true); }; window.addEventListener('canntalk-auth-expired', expire); return () => window.removeEventListener('canntalk-auth-expired', expire); }, []);

  if (loading) return <div className="app-loading"><span className="loading-bars"><i /><i /><i /></span><p>正在準備學習體驗<br /><small>Preparing your learning experience</small></p></div>;
  if (locked || !identity) return <AccessPage onUnlocked={() => load(true)} />;

  const handleLogout = async () => { try { await logout(); } finally { setIdentity(null); setBootstrap(null); setLocked(true); navigate('/dashboard', { replace: true }); } };
  const handleReset = async () => { await resetDemo(); await load(); };
  const refreshIdentity = async () => { const current = await getIdentity(); setIdentity(current); if (!current.user.mustChangePassword) setBootstrap(await getBootstrap()); };
  const identityContext = { identity, refreshIdentity, signOut: handleLogout };
  if (identity.user.mustChangePassword) return <IdentityProvider value={identityContext}><AccountPage required /></IdentityProvider>;
  if (!bootstrap) return <div className="app-loading" role="status">正在載入帳戶 Loading account…</div>;

  return (
    <IdentityProvider value={identityContext}><CanNTalkProvider><Routes>
      <Route element={<AppShell bootstrap={bootstrap} onLogout={handleLogout} onReset={handleReset} />}>
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="today" element={<Navigate to="/dashboard" replace />} />
        <Route path="dashboard" element={<PlacementGate><PracticeHome /></PlacementGate>} />
        <Route path="scenarios" element={<PlacementGate><ScenarioCatalogue /></PlacementGate>} />
        <Route path="setup/:scenarioId" element={<PlacementGate><SessionSetup /></PlacementGate>} />
        <Route path="practice/:sessionId" element={<PlacementGate><LivePractice /></PlacementGate>} />
        <Route path="report/:sessionId" element={<SessionReport />} />
        <Route path="progress" element={<PracticeProgress />} />
        <Route path="account" element={<AccountPage />} />
        <Route path="teaching" element={<RoleRoute roles={['teacher', 'admin']}><TeachingDashboard /></RoleRoute>} />
        <Route path="manage/users" element={<RoleRoute roles={['admin']}><IdentityManagement /></RoleRoute>} />
        <Route path="manage/classes" element={<RoleRoute roles={['admin']}><IdentityManagement /></RoleRoute>} />
        <Route path="manage/scenarios" element={<ScenarioStudio />} />
        <Route path="manage/scenarios/:scenarioId" element={<ScenarioStudio />} />
        <Route path="manage/ai" element={<AIConnectionSettings />} />
        <Route path="placement" element={<PlacementPage />} />
        <Route path="learn" element={<Navigate to="/scenarios" replace />} />
        <Route path="ask" element={<Navigate to="/scenarios" replace />} />
        <Route path="chat" element={<Navigate to="/scenarios" replace />} />
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Route>
    </Routes></CanNTalkProvider></IdentityProvider>
  );
}
